import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import tableEmptyStateAdd from '@/assets/images/table/tableEmptyStateInvoice.svg'
import {
    Button,
    Card,
    CompanyFilter,
    DatePicker,
    IconExpand01,
    IconDownload,
    IconMinimize01,
    Image,
    PageHeader
} from '@/components'
import { AuthLayout, IndexContainer, WarrantySummaryItemIndex } from '@/containers'
import { useAuth, useIndexContainer, useAnalytics } from '@/hooks'
import { Invoice, Model } from '@/models'
import {
    DateRangeType,
    IndexContainerChangeEvent, IndexContainerFilterValueType,
    InvoiceIndexDataType,
    InvoiceIndexFiltersType
} from '@/types'

const INVOICE_INDEX_FILTERS: InvoiceIndexFiltersType = {
    search: {
        label: 'Search',
        defaultValue: '',
        value: '',
        isStandalone: true
    },
    date_range: {
        label: 'Date',
        defaultValue: [new Date().startOf('month'), new Date().endOf('month')],
        value: [new Date().startOf('month'), new Date().endOf('month')],
        isStandalone: true,
        convert: (value: IndexContainerFilterValueType) => value
            ? (value as [string, string]).map(Model.toDate) as DateRangeType
            : [new Date().startOf('month'), new Date().endOf('month')]
    },
    company_id: {
        label: 'Company',
        defaultValue: '',
        value: '',
        isStandalone: true
    }
}

export const InvoicesIndex = () => {
    const auth = useAuth()
    const { trackEvent } = useAnalytics()

    const [isInfoOpen, setIsInfoOpen] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()

    const raw = searchParams.get('invoice_records_expanded')
    const isExpanded = raw === null || raw === 'true'

    const {
        filters,
        handleFiltersChange
    } = useIndexContainer(INVOICE_INDEX_FILTERS, 'Invoice Records')

    const [data, setData] = useState<InvoiceIndexDataType>({
        meta: {
            total: 0,
            overall_count: 0,
            current_page: 0
        },
        rows: [],
        selectedRows: []
    })

    const handleChange = (e: IndexContainerChangeEvent) => {
        setData(data => ({
            ...data,
            [e.name]: e.value
        }))
    }

    const selected = useMemo<Invoice[] | 'all'>(() => {
        if (data.selectedRows === 'all') return 'all'
        if (Array.isArray(data.selectedRows)) {
            return data.selectedRows
                .map(item => data.rows.find(row => row.id === item.id))
                .filter((row): row is Invoice => !!row)
        }
        return []
    }, [data.rows, data.selectedRows])

    const handleDownloadClick = () => {
        if (selected === 'all') {
            Invoice.download([], {
                select_all: true,
                ...Object.keys(filters).reduce((acc, key) => ({
                    ...acc,
                    [key]: filters[key].value?.toString() === filters[key].defaultValue?.toString()
                        ? null : filters[key].value
                }), {})
            })
            return
        }

        Invoice.download(selected)
    }

    const rowsMemo = useMemo(() => data.rows.map(item => ({
        id: item.id,
        accounting_period: item.accounting_period.format({
            day: null,
            year: 'numeric'
        }),
        company_name: item.company_name,
        type: item.type.title,
        invoice_number: item.invoice_number,
        policy_total_count: item.policy_total_count.format(),
        battery_total_count: item.battery_total_count.format(),
        panel_total_count: item.panel_total_count.format(),
        total_price: item.total_price < 0
            ? `(${Math.abs(item.total_price).money()})`
            : item.total_price.money(),
        balance: item.balance_due < 0
            ? `(${Math.abs(item.balance_due).money()})`
            : item.balance_due.money(),
        payment_status: <span className="text-xs">{item.payment_status.badge}</span>,
        _path: `/invoices/${item.id}`
    })), [data.rows])

    const handleToggleInfoClick = () => {
        trackEvent('button_click', 'invoice_records', 'Toggle Info Button')
        setIsInfoOpen(isInfoOpen => !isInfoOpen)
    }

    const handleToggleClick = () => {
        trackEvent('button_click', 'invoice_records', 'Toggle Button')
        const next = (!isExpanded).toString()
        searchParams.set('invoice_records_expanded', next)
        setSearchParams(searchParams)
    }

    return <AuthLayout heading={<PageHeader title="Invoice Records"/>}>
        <div className="flex flex-col gap-6">
            <Card>
                <div className="flex justify-between">
                    <h2 className="text-2xl flex flex-wrap gap-2">
                        Invoice Records
                        <Button
                            design="btn-link"
                            className="text-primary-800"
                            onClick={handleToggleInfoClick}
                        >
                            {isInfoOpen ? 'Show Less' : 'Learn More'}
                        </Button>
                    </h2>

                    <Button
                        square
                        design="btn-secondary-gray"
                        className="shrink-0"
                        onClick={handleToggleClick}
                        tooltip={isExpanded? 'Minimize' : 'Maximize'}
                    >
                        {isExpanded
                            ? <IconMinimize01 className="animate-fade-in"/>
                            : <IconExpand01 className="animate-fade-in"/>}
                    </Button>
                </div>
                {isInfoOpen && <div
                    className="rounded-lg animate-fade-in p-4 bg-gray-100 text-sm text-gray-750 flex flex-col gap-2.5 mt-2"
                >
                    <p>
                        <span className="font-semibold">📌 Description:</span> This table provides a frozen snapshot of
                        your issued invoices, showing exactly what was billed at the time of invoicing. These records do
                        not change, even if adjustments (such as size edits or cancellations) occur later. Any changes
                        made after invoicing will appear as credits or debits on future invoices.
                    </p>
                    <p>
                        <span className="font-semibold">💳 Payments:</span> Invoice payments are not processed in
                        Daybreak. Please check your inbox for emails from accounting@solarinsure.com for payment details
                        and outstanding balances. All payments are securely handled through a secure external payment
                        gateway.
                    </p>
                    <p>
                        <span className="font-semibold">🛟 Need Help?</span> Contact <a
                            href="mailto:accounting@solarinsure.com"
                            className="underline italic"
                        >accounting@solarinsure.com</a> or your account manager for assistance.
                    </p>
                </div>}
                {isExpanded && <IndexContainer
                    id="invoices"
                    dataName="Invoices"
                    className="animate-fade-in mt-6"
                    api={(params, ...args) =>
                        Invoice.index({ activity_tracker: 'invoice.index', ...params }, ...args)}
                    data={data}
                    onDataChange={handleChange}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    tableOptions={{
                        dataName: 'Invoices',
                        selectable: true
                    }}
                    columns={[
                        { field: 'accounting_period', title: 'Accounting Period' },
                        {
                            field: 'invoice_number',
                            title: 'Invoice #',
                            showIf: auth.user?.role.isAdminOrStaffOrAdvisor
                        },
                        {
                            field: 'company_name',
                            title: 'Company',
                            showIf: auth.user?.role.isAdminOrStaffOrAdvisor
                        },
                        { field: 'type', title: 'Type' },
                        {
                            field: 'invoice_number',
                            title: 'Invoice #',
                            showIf: auth.user?.role.isAffiliateOrCertifiedProvider
                        },
                        { field: 'policy_total_count', title: '# of Policies' },
                        {
                            field: 'battery_total_count',
                            title: '# of Batteries',
                            showIf: auth.user?.role.isAffiliateOrCertifiedProvider
                        },
                        {
                            field: 'panel_total_count',
                            title: '# of Panels',
                            showIf: auth.user?.role.isAffiliateOrCertifiedProvider
                        },
                        { field: 'total_price', title: 'Total' },
                        { field: 'balance', title: 'Balance' },
                        { field: 'payment_status', title: 'Status' }
                    ]}
                    rows={rowsMemo}
                    leftSide={<>
                        <DatePicker
                            id="invoice-index-date-range-filter"
                            name="date_range"
                            placeholder="Date range"
                            value={filters.date_range.value}
                            onChange={handleFiltersChange}
                            singleMode={false}
                            maxDate={new Date().endOf('month')}
                            className="md:min-w-75"
                        />
                        {!auth.user?.role.isCertifiedProvider && <CompanyFilter
                            id="invoice-index-company-id"
                            name="company_id"
                            placeholder="Company"
                            onChange={handleFiltersChange}
                            value={filters.company_id.value}
                            className="md:min-w-75"
                        />}
                    </>}
                    rightSide={<>
                        <Button
                            square
                            onClick={() => handleDownloadClick()}
                            disabled={!data.selectedRows?.length}
                            design="btn-secondary-gray"
                            size="btn-lg"
                            tooltip={data.selectedRows?.length
                                ? 'Download Invoice Data' : 'Select at least one row to download data.'}
                            tooltipDisabled={false}
                        >
                            <IconDownload/>
                        </Button>
                    </>}
                    placeholder={data.meta?.overall_count === 0 && <>
                        <Image
                            className="w-30"
                            src={tableEmptyStateAdd}
                        />
                        <div className="mt-4 flex flex-col items-center">
                            <h4 className="text-center font-light">Nothing to Invoice, Nothing to Worry!</h4>
                            <p className="text-center text-gray-500 font-light text-sm mt-1">
                                There are currently no invoices to display. You{'\''}re all set! We{'\''}ll start
                                populating this table once you{'\''}ve been billed. In the meantime, feel free to
                                explore
                                our other features and let us know if you have any questions.
                            </p>
                        </div>
                    </>}
                />}
            </Card>

            <WarrantySummaryItemIndex/>
        </div>
    </AuthLayout>
}
